import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import { WorkflowBoard } from "../dtos";
import { closureSummary } from "../utils/closure-summary";
import CaseStatusBadge from "./case-status-badge";
import { holdSummary } from "../utils/hold-summary";

const PILL_TEXT = {
  fontSize: 11,
  lineHeight: "18px",
  fontWeight: 600,
  whiteSpace: "nowrap",
} as const;

/**
 * §CaseVerdict + §Case-hold: the WHOLE-application pills beside the "Loan process" title — the WM funnel badge
 * (label + color), the lifecycle chip on a terminal case, and the amber "On hold" flag while a WM super admin
 * has the application paused. Extracted from the tab header so its render stays under the complexity gate.
 */
const CaseStatusPills = ({ board }: { board: WorkflowBoard | null | undefined }) => {
  const theme = useTheme();
  if (!board) return null;
  const badgeColor = (board.statusBadge as { color?: string } | null)?.color;
  const terminal = board.caseStatus === "CANCELLED" || board.caseStatus === "COMPLETED";
  return (
    <>
      {board.categoryStatuses ? (
        <CaseStatusBadge statusBadge={board.statusBadge} caseStatus={board.caseStatus}
          categoryStatuses={board.categoryStatuses} />
      ) : board.statusBadge?.label && (
        <Box
          sx={{
            px: "6px",
            py: 0,
            borderRadius: theme.nawy.radius.md,
            backgroundColor: typeof badgeColor === "string" ? `${badgeColor}26` : "#FFC8C2",
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          <Typography sx={{ ...PILL_TEXT, whiteSpace: "normal", overflowWrap: "anywhere", color: badgeColor ?? "#AB2018" }}>
            {board.statusBadge.label}
          </Typography>
        </Box>
      )}
      {terminal && (
        // §Flow-closed-note: the Cancelled chip carries who/where/why in its tip; the banner above the tabs
        // spells it out (a closed flow has nothing else to announce).
        <Tooltip title={board.closure ? closureSummary(board.closure) : ""}>
          <Box
            sx={{
              px: "6px",
              py: 0,
              borderRadius: theme.nawy.radius.md,
              backgroundColor: board.caseStatus === "CANCELLED" ? "#E0E0E0" : "#CFE8E2",
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{ ...PILL_TEXT, color: board.caseStatus === "CANCELLED" ? "#313131" : "#326054" }}
            >
              {board.caseStatus === "CANCELLED" ? "Cancelled" : "Completed"}
            </Typography>
          </Box>
        </Tooltip>
      )}
      {/* §Case-hold: paused by a WM super admin — visible, but every step is read-only until resumed. */}
      {board.onHold && (
        <Tooltip title={holdSummary(board.onHold)}>
          <Box
            sx={{
              px: "6px",
              py: 0,
              borderRadius: theme.nawy.radius.md,
              backgroundColor: "#FFF3CD",
              flexShrink: 0,
            }}
          >
            <Typography sx={{ ...PILL_TEXT, color: "#8A5A00" }}>On hold</Typography>
          </Box>
        </Tooltip>
      )}
    </>
  );
};

export default CaseStatusPills;
